import { useEffect } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { useEntitlements } from "../../context/EntitlementsContext";
import UpgradePrompt from "./UpgradePrompt";

// Wraps a page/section that a subscription plan may not include. While
// entitlements are loading we render nothing rather than a flash of the
// locked state. If the plan couldn't be fetched at all (e.g. the backend was
// down), we show a retry state instead of the upgrade prompt — "couldn't
// check" must never look like "your plan doesn't include this".
export default function RequireFeature({ feature, title, message, className, children }) {
  const { entitlements, loading, error, refresh } = useEntitlements();

  // The plan is fetched once when the app loads; if that attempt failed,
  // opening a gated page tries again instead of staying locked until reload.
  useEffect(() => {
    if (!loading && (error || !entitlements)) refresh();
    // Only on mount — re-running on every change would loop on a persistent failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return null;

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white p-10 text-center ${className || ""}`} dir="rtl">
        <AlertTriangle size={22} className="text-amber-600" aria-hidden="true" />
        <p className="text-sm font-bold text-gray-800">اطلاعات اشتراک شما دریافت نشد.</p>
        <p className="max-w-sm text-xs text-gray-500">ارتباط با سرور برقرار نشد. اتصال را بررسی کنید و دوباره تلاش کنید.</p>
        <button
          type="button"
          onClick={refresh}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-secondary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          <RefreshCcw size={15} aria-hidden="true" />
          تلاش دوباره
        </button>
      </div>
    );
  }

  const allowed = Boolean(entitlements?.features?.[feature]);
  if (!allowed) {
    return <UpgradePrompt title={title} message={message} className={className} />;
  }

  return children;
}
